import { DateTime } from 'luxon';
import { t } from './translation.helper';

export class DateHelper {
  static isToday(date: DateTime): boolean {
    return date.hasSame(DateTime.utc(), 'day');
  }

  static isYesterday(date: DateTime): boolean {
    return date.hasSame(DateTime.utc().minus({ days: 1 }), 'day');
  }

  static isTomorrow(date: DateTime): boolean {
    return date.hasSame(DateTime.utc().plus({ days: 1 }), 'day');
  }

  static isSameHour(date: DateTime, compareDate: DateTime = DateTime.utc()): boolean {
    return date.hasSame(compareDate, 'hour');
  }

  static isSameYear(date: DateTime, compareDate: DateTime = DateTime.utc()): boolean {
    return date.hasSame(compareDate, 'year');
  }

  static isSameDay(date: DateTime, compareDate: DateTime): boolean {
    return date.hasSame(compareDate, 'day');
  }

  static shortDate(date: DateTime, format: string): string {
    if (this.isToday(date)) {
      return t('Today');
    }
    if (this.isYesterday(date)) {
      return t('Yesterday');
    }
    return date.toFormat(format);
  }

  static formattedDate(date: string): string {
    if (DateHelper.isToday(DateTime.fromISO(date))) {
      return t('Today').toString();
    }
    if (DateHelper.isYesterday(DateTime.fromISO(date))) {
      return t('Yesterday').toString();
    }
    if (DateHelper.isSameYear(DateTime.fromISO(date))) {
      return DateTime.fromISO(date).toLocaleString({
        day: 'numeric',
        month: 'short',
      });
    }
    return DateTime.fromISO(date).toLocaleString({ ...DateTime.DATE_MED });
  }

  static formattedDateFull(date: string): string {
    if (DateHelper.isToday(DateTime.fromISO(date))) {
      return t('Today').toString();
    }
    if (DateHelper.isYesterday(DateTime.fromISO(date))) {
      return t('Yesterday').toString();
    }

    return DateTime.fromISO(date).toLocaleString({ ...DateTime.DATE_FULL });
  }

  static differenceInDays(date: DateTime): number {
    return Math.ceil(date.diff(DateTime.now(), 'days').toObject().days);
  }

  static relativeTime(date: DateTime): string {
    if (DateHelper.isToday(date)) {
      return t('Today').toString();
    }
    if (DateHelper.isYesterday(date)) {
      return t('Yesterday').toString();
    }
    if (DateHelper.isTomorrow(date)) {
      return t('tomorrow').toString();
    }

    const daysLeft: number = DateHelper.differenceInDays(date);
    return daysLeft < 0
      ? t('days-ago', { daysAgo: Math.abs(daysLeft) }).toString()
      : t('days-left', { daysLeft }).toString();
  }

  static years(offset = 0): number[] {
    const startYear = 1970;
    const numberOfYears: number = DateTime.utc().plus({ years: offset }).year - startYear;
    return [...Array(numberOfYears).keys()].map((year: number) => year + startYear).reverse();
  }

  static disableDateTo(date: string): { to?: Date } {
    return {
      to: DateTime.fromISO(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate(),
    };
  }
}
